import React, { useState } from 'react';
import Navbar from '../../components/layout/Navbar';
import Footer from '../../components/layout/Footer';
import PageHeader from '../../components/common/PageHeader';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Check } from 'lucide-react';
import LearnEnrollmentForm from '../../components/learn/LearnEnrollmentForm';

const LearnPricing: React.FC = () => {
  const [enrollmentOpen, setEnrollmentOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<'online' | 'faceToFace'>('online');
  const [redirecting, setRedirecting] = useState(false);

  const handleEnroll = (productType: 'online' | 'faceToFace') => {
    setSelectedProduct(productType);
    setEnrollmentOpen(true);
  };

  return (
    <>
      <Navbar />
      <main className="min-h-screen pt-16">
        <PageHeader 
          title="Pricing"
          subtitle="Choose the learning format that works best for you"
        />
        <div className="py-12 bg-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
            <div className="grid md:grid-cols-2 gap-8">
              {/* Online Course */}
              <Card className="p-8 border-2 border-gray-200 hover:border-brand-teal transition-colors">
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-brand-navy mb-2">Online Course</h3>
                  <div className="mb-4">
                    <span className="text-5xl font-bold text-brand-teal">$50</span>
                    <span className="text-gray-600 ml-2">+ GST</span>
                  </div>
                  <p className="text-gray-600">Self-paced online learning</p>
                </div>

                <ul className="space-y-4 mb-8">
                  <li className="flex items-start">
                    <Check className="w-5 h-5 text-brand-teal mt-0.5 mr-3 flex-shrink-0" />
                    <span className="text-gray-700">Full course with 16 modules</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="w-5 h-5 text-brand-teal mt-0.5 mr-3 flex-shrink-0" />
                    <span className="text-gray-700">Lifetime access to all materials</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="w-5 h-5 text-brand-teal mt-0.5 mr-3 flex-shrink-0" />
                    <span className="text-gray-700">Downloadable resources & templates</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="w-5 h-5 text-brand-teal mt-0.5 mr-3 flex-shrink-0" />
                    <span className="text-gray-700">Video lessons with captions & transcripts</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="w-5 h-5 text-brand-teal mt-0.5 mr-3 flex-shrink-0" />
                    <span className="text-gray-700">Mastery-based quizzes (80% pass)</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="w-5 h-5 text-brand-teal mt-0.5 mr-3 flex-shrink-0" />
                    <span className="text-gray-700">2 capstone projects with feedback</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="w-5 h-5 text-brand-teal mt-0.5 mr-3 flex-shrink-0" />
                    <span className="text-gray-700">Chamber-endorsed certificate</span>
                  </li>
                </ul>

                <Button className="w-full" size="lg" onClick={() => handleEnroll('online')}>
                  Enroll Online - $50 + GST
                </Button>
                <p className="text-xs text-center mt-3 text-gray-500">
                  Secure payment via Stripe
                </p>
              </Card>

              {/* F2F Workshop */}
              <Card className="p-8 border-2 border-brand-teal bg-brand-teal/5">
                <div className="text-center mb-6">
                  <div className="inline-block bg-brand-teal text-white px-3 py-1 rounded-full text-xs font-semibold mb-3">
                    LIMITED AVAILABILITY
                  </div>
                  <h3 className="text-2xl font-bold text-brand-navy mb-2">Face-to-Face Workshop</h3>
                  <div className="mb-4">
                    <span className="text-5xl font-bold text-brand-teal">$200</span>
                    <span className="text-gray-600 ml-2">+ GST</span>
                  </div>
                  <p className="text-gray-600">In-person training (max 20 attendees)</p>
                </div>

                <ul className="space-y-4 mb-8">
                  <li className="flex items-start">
                    <Check className="w-5 h-5 text-brand-teal mt-0.5 mr-3 flex-shrink-0" />
                    <span className="text-gray-700">Everything in Online Course</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="w-5 h-5 text-brand-teal mt-0.5 mr-3 flex-shrink-0" />
                    <span className="text-gray-700">Full-day in-person workshop</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="w-5 h-5 text-brand-teal mt-0.5 mr-3 flex-shrink-0" />
                    <span className="text-gray-700">Live hands-on exercises</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="w-5 h-5 text-brand-teal mt-0.5 mr-3 flex-shrink-0" />
                    <span className="text-gray-700">Direct Q&A with trainer</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="w-5 h-5 text-brand-teal mt-0.5 mr-3 flex-shrink-0" />
                    <span className="text-gray-700">Networking with other SME owners</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="w-5 h-5 text-brand-teal mt-0.5 mr-3 flex-shrink-0" />
                    <span className="text-gray-700">Premium workshop materials</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="w-5 h-5 text-brand-teal mt-0.5 mr-3 flex-shrink-0" />
                    <span className="text-gray-700">Priority capstone grading</span>
                  </li>
                </ul>

                <Button className="w-full" size="lg" variant="default" onClick={() => handleEnroll('faceToFace')}>
                  Enroll in Workshop - $200 + GST
                </Button>
                <p className="text-xs text-center mt-3 text-gray-500">
                  Limited to 20 seats per session
                </p>
              </Card>
            </div>

            {/* Additional Info */}
            <div className="mt-12 bg-gray-50 rounded-lg p-8 border border-gray-200">
              <h3 className="text-xl font-semibold mb-4 text-brand-navy text-center">
                What's Included in Both Options
              </h3>
              <div className="grid sm:grid-cols-3 gap-6 text-center">
                <div>
                  <p className="font-semibold text-brand-navy mb-2">16 Modules</p>
                  <p className="text-sm text-gray-600">Comprehensive curriculum covering foundations to advanced adoption</p>
                </div>
                <div>
                  <p className="font-semibold text-brand-navy mb-2">6 Automation Patterns</p>
                  <p className="text-sm text-gray-600">With MS/Google/Xero/GHL variants for each</p>
                </div>
                <div>
                  <p className="font-semibold text-brand-navy mb-2">Chamber Certificate</p>
                  <p className="text-sm text-gray-600">Recognized credential upon successful completion</p>
                </div>
              </div>
            </div>

            {/* FAQ */}
            <div className="mt-12">
              <h3 className="text-2xl font-bold mb-6 text-brand-navy text-center">
                Frequently Asked Questions
              </h3>
              <div className="space-y-4">
                <div className="bg-white p-6 rounded-lg border border-gray-200">
                  <h4 className="font-semibold text-brand-navy mb-2">How long does the course take?</h4>
                  <p className="text-gray-700 text-sm">
                    The online course is self-paced. Most learners complete it in 4-6 weeks, 
                    dedicating 2-3 hours per week. F2F workshops are full-day sessions.
                  </p>
                </div>
                <div className="bg-white p-6 rounded-lg border border-gray-200">
                  <h4 className="font-semibold text-brand-navy mb-2">What if I need a refund?</h4>
                  <p className="text-gray-700 text-sm">
                    We offer a 14-day money-back guarantee if you're not satisfied with the course content. 
                    Contact us at connect@thinktomorrow.ai.
                  </p>
                </div>
                <div className="bg-white p-6 rounded-lg border border-gray-200">
                  <h4 className="font-semibold text-brand-navy mb-2">Do I need any technical skills?</h4>
                  <p className="text-gray-700 text-sm">
                    No advanced technical skills required. If you can use email and browse the web, 
                    you can succeed in this course. We start from the basics.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
      
      <LearnEnrollmentForm
        isOpen={enrollmentOpen}
        onClose={() => setEnrollmentOpen(false)}
        productType={selectedProduct}
        onRedirecting={() => setRedirecting(true)}
      />

      {/* Redirect Loading Overlay */}
      {redirecting && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 text-center max-w-sm mx-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-teal mx-auto mb-4"></div>
            <h3 className="text-xl font-semibold text-brand-navy mb-2">Redirecting to Stripe...</h3>
            <p className="text-gray-600 text-sm">Please wait while we redirect you to complete your payment.</p>
          </div>
        </div>
      )}
    </>
  );
};

export default LearnPricing;
